import React from "react";
import { useVisitTracking } from "../../context/VisitTrackingContext";
import { getDisplaySubtitle } from "../../data/pathLabels";
import "./TimelineModal.scss";

function formatDuration(ms) {
  if (ms < 1000) return `${ms}ms`;
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const s = sec % 60;
  if (min < 60) return `${min}m ${s}s`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${m}m`;
}

/** dd/MM/yyyy hh:mm tt */
function formatDateTime(timestamp) {
  const d = new Date(timestamp);
  const dd = String(d.getDate()).padStart(2, "0");
  const MM = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh12 = d.getHours() % 12 || 12;
  const hh = String(hh12).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const tt = d.getHours() < 12 ? "AM" : "PM";
  return `${dd}/${MM}/${yyyy} ${hh}:${mm} ${tt}`;
}

const TimelineModal = ({ isOpen, onClose }) => {
  const { visitHistory } = useVisitTracking();

  const entries = React.useMemo(() => {
    return [...(visitHistory || [])].sort((a, b) => (b.visitedAt || 0) - (a.visitedAt || 0));
  }, [visitHistory]);

  if (!isOpen) return null;

  const maxTime = Math.max(...entries.map((e) => e.timeSpentMs || 0), 1);

  const getTimeSpentColor = (ms) => {
    if (maxTime <= 0) return "#dc3545";
    const pct = ((ms || 0) / maxTime) * 100;
    if (pct < 33) return "#dc3545";
    if (pct < 66) return "#fd7e14";
    return "#198754";
  };

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label="Activity timeline"
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-chart-line me-2" aria-hidden="true" />
              Activity timeline
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>
          <div className="modal-body timeline-modal-body-scroll">
            {entries.length === 0 ? (
              <div className="timeline-modal-empty text-center text-muted py-4">
                <i className="fas fa-inbox fa-3x mb-3 d-block" aria-hidden="true" />
                <p className="mb-0">No activity data yet. Navigate around the app to see your timeline here.</p>
              </div>
            ) : (
              <div className="timeline-vertical">
                {entries.map((entry, index) => {
                  const displaySubtitle = getDisplaySubtitle(entry.subtitle);
                  const label = displaySubtitle ? `${entry.label} · ${displaySubtitle}` : entry.label;
                  return (
                    <div key={`${entry.path}-${entry.visitedAt}-${index}`} className="timeline-item">
                      <div
                        className="timeline-node"
                        style={{
                          backgroundColor: getTimeSpentColor(entry.timeSpentMs),
                          boxShadow: `0 0 0 3px ${getTimeSpentColor(entry.timeSpentMs)}33`,
                        }}
                        aria-hidden="true"
                      />
                      <div className="timeline-card">
                        <div className="timeline-card-header">
                          <span className="timeline-card-label">{label}</span>
                        </div>
                        {entry.visitedAt != null && (
                          <div className="timeline-card-datetime">
                            <i className="far fa-calendar-alt me-1" aria-hidden="true" />
                            {formatDateTime(entry.visitedAt)}
                          </div>
                        )}
                        <div className="timeline-card-stats">
                          <span className="timeline-stat">
                            <i className="far fa-clock" aria-hidden="true" />
                            {formatDuration(entry.timeSpentMs || 0)}
                          </span>
                          <span className="timeline-stat">
                            <i className="fas fa-mouse-pointer" aria-hidden="true" />
                            {entry.clickCount ?? 0} clicks
                          </span>
                          <span className="timeline-stat">
                            <i className="fas fa-tachometer-alt" aria-hidden="true" />
                            Load {entry.loadTimeMs != null && entry.loadTimeMs > 0 ? formatDuration(entry.loadTimeMs) : "—"}
                          </span>
                          <span className={`timeline-stat ${(entry.apiErrorCount || 0) > 0 ? "timeline-stat-error" : ""}`}>
                            <i className="fas fa-exclamation-triangle" aria-hidden="true" />
                            API errors {(entry.apiErrorCount ?? 0)}
                          </span>
                          <span className={`timeline-stat ${(entry.networkErrorCount || 0) > 0 ? "timeline-stat-error" : ""}`}>
                            <i className="fas fa-wifi" aria-hidden="true" />
                            Network errors {(entry.networkErrorCount ?? 0)}
                          </span>
                        </div>
                        <div
                          className="timeline-card-bar"
                          style={{
                            width: `${Math.min(100, ((entry.timeSpentMs || 0) / maxTime) * 100)}%`,
                            backgroundColor: getTimeSpentColor(entry.timeSpentMs),
                          }}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineModal;
